import React from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'
import type { BadgeProps } from '@wartek-id/badge'

import { apiPaths } from 'api/kinerja/pengawas-sekolah/config'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPISubmitPerencanaan } from 'api/kinerja/pengawas-sekolah/perencanaan'
import { Card, CardBody } from 'app/KinerjaShared/components/Card'
import { Header } from 'app/KinerjaShared/components/Header'
import { CustomTabSteps } from 'app/KinerjaPSPegawaiPage/components/CustomTabSteps'
import { CardSelectionPraktikKinerja } from 'app/KinerjaPSPegawaiPage/components/CardSelectionPraktikKinerja'
import { CardSummaryPraktikKinerja } from 'app/KinerjaPSPegawaiPage/components/CardSummaryPraktikKinerja'
import { DialogPraktikKinerjaConfirmation } from 'app/KinerjaPSPegawaiPage/components/DialogPraktikKinerjaConfirmation'
import { DialogExitPrevention } from 'app/KinerjaPSPegawaiPage/components/DialogExitPrevention'
import { DialogPraktikKinerjaInfo } from 'app/KinerjaPSPegawaiPage/components/DialogPraktikKinerjaInfo'
import { DialogUpdateSatuanPendidikanInfo } from 'app/KinerjaPSPegawaiPage/components/DialogUpdateSatuanPendidikanInfo'
import { FooterActionV2 } from 'app/KinerjaPSPegawaiPage/components/FooterActionV2'
import ScrollDown from 'app/KinerjaPSPegawaiPage/components/ScrollDown'
import {
  PRAKTIK_KINERJA_HINTS,
  PRAKTIK_KINERJA_MAX_SATUAN_PENDIDIKAN_SELECTION,
  SCORE_COLORS,
  SCORE_TRANSLATION,
} from 'app/KinerjaPSPegawaiPage/constants/praktikKinerja'
import { usePraktikKinerja } from 'app/KinerjaPSPegawaiPage/hooks/usePraktikKinerja'
import { useSatuanPendidikan } from 'app/KinerjaPSPegawaiPage/hooks/useSatuanPendidikan'
import { usePerencanaanPS } from 'app/KinerjaPSPegawaiPage/hooks/usePerencanaan'
import { usePraktikKinerjaDraftStore } from 'app/KinerjaPSPegawaiPage/stores/kinerja-ps'
import useWarn from 'app/KinerjaShared/hooks/useWarn'
import styles from 'app/KinerjaPSPegawaiPage/pages/Perencanaan/PraktikKinerja/PraktikKinerja.module.css'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useStoreByUserId } from 'app/KinerjaPSPegawaiPage/hooks/useStoreByUserId'
import usePrevious from 'utils/hooks/usePrevious'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type {
  TPraktikKinerjaForm,
  TPerencanaanPraktikKinerjaRequestBody,
} from 'types/kinerja-ps-pegawai'

export const TabContentPraktikKinerja = () => {
  const { control } = useFormContext()

  const { praktikKinerjaData } = usePraktikKinerja()
  const { isPraktikKinerjaEditable } = usePerencanaanPS()

  if (praktikKinerjaData.length === 0) {
    return null
  }

  return (
    <div className="space-y-6">
      <Text variant="heading-lg">
        Pilih 1 praktik untuk menjadi fokus peningkatan kinerja Anda
      </Text>

      <Controller
        control={control}
        name="selectedPraktikKinerjaId"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value } }) => (
          <>
            {praktikKinerjaData.map((item, index) => (
              <CardSelectionPraktikKinerja
                {...item}
                key={index}
                isActive={value === item.id}
                isDisabled={!isPraktikKinerjaEditable}
                onClick={() => onChange(item.id)}
              />
            ))}
          </>
        )}
      />
    </div>
  )
}

type TTabContentSatuanPendidikanProps = {
  remainingSelection: number
  isValid: boolean
  onBack: () => void
}
const TabContentSatuanPendidikan: React.FC<
  TTabContentSatuanPendidikanProps
> = ({ remainingSelection, isValid, onBack }) => {
  const toast = useToast()

  const [isDialogUpdateInfoOpen, setDialogUpdateInfoOpen] =
    React.useState<boolean>(false)
  const { control, watch } = useFormContext()
  const selectedPraktikKinerjaId = watch('selectedPraktikKinerjaId')
  const selectedSatuanPendidikanIds = watch('selectedSatuanPendidikanIds')

  const { praktikKinerjaData } = usePraktikKinerja()
  const { isPraktikKinerjaEditable } = usePerencanaanPS()

  const selectedPraktikKinerja = praktikKinerjaData.find(
    ({ id }) => id === selectedPraktikKinerjaId
  )
  const { satuanPendidikanData } = useSatuanPendidikan({
    rhkId: selectedPraktikKinerjaId,
  })

  const isOptionsEmpty = React.useMemo(
    () =>
      !Boolean(selectedPraktikKinerjaId) || satuanPendidikanData.length === 0,
    [satuanPendidikanData.length, selectedPraktikKinerjaId]
  )

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Text variant="heading-lg">
          Pilih 3 satuan pendidikan untuk menjadi fokus dalam melakukan Praktik
          Kinerja Anda
        </Text>

        <Text>
          Anda tetap diharapkan untuk mendampingi semua satuan pendidikan, 3
          satuan pendidikan yang dipilih hanya untuk fokus Pengelolaan Kinerja
          saja.
        </Text>
      </div>

      {isOptionsEmpty ? (
        <div className="flex justify-between p-3 rounded-md bg-surface-selected-default">
          <div className="flex space-x-3">
            <div className="flex-none">
              <Icon color="informational">info_outline</Icon>
            </div>
            <Text color="informational">
              Pilih Praktik Kinerja terlebih dahulu.
            </Text>
          </div>

          <div>
            <Button color="black" size="sm" onClick={onBack}>
              Pilih Praktik Kinerja
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between p-3 space-y-3 rounded-md bg-surface-selected-default">
            <div className="space-y-2">
              <Text color="subdued">Praktik Kinerja yang Anda pilih:</Text>
              <Text variant="heading-sm">{selectedPraktikKinerja.nama}</Text>
            </div>
            {isPraktikKinerjaEditable && (
              <div>
                <Button
                  color="white"
                  size="sm"
                  className="!bg-surface-selected-default"
                  onClick={() => onBack()}
                >
                  Ubah
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Text>
                Berikut {satuanPendidikanData.length} satuan pendidikan binaan
                Anda:
              </Text>
              <Button
                variant="ghost"
                className="space-x-1"
                onClick={() => setDialogUpdateInfoOpen(true)}
              >
                <Icon color="informational" fontSize="sm">
                  help
                </Icon>
                <span>Cara perbarui data satuan pendidikan</span>
              </Button>
            </div>
            {isPraktikKinerjaEditable && (
              <div>
                <Text
                  color={isValid ? 'success' : 'warning'}
                  variant="action"
                  className="flex items-center space-x-1"
                >
                  <span>
                    {isValid
                      ? `${selectedSatuanPendidikanIds.length} satuan pendidikan sudah dipilih`
                      : `Pilih ${remainingSelection} satuan pendidikan lagi`}
                  </span>
                  <Icon color={isValid ? 'success' : 'warning'}>
                    {isValid ? 'check' : 'info'}
                  </Icon>
                </Text>
              </div>
            )}
          </div>

          <table className="w-full">
            <thead className="font-bold border-b bg-surface-subdued border-subdued">
              <tr>
                <td className="p-4">Nama Satuan Pendidikan</td>
                <td className="p-4">
                  Rapor Pendidikan {selectedPraktikKinerja.nama}
                </td>
                <td className="p-4" width="35%"></td>
              </tr>
            </thead>
            <tbody>
              <Controller
                control={control}
                name="selectedSatuanPendidikanIds"
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => {
                  const handleChange = (id: string) => {
                    if (!isPraktikKinerjaEditable) {
                      return
                    }

                    if (
                      value.length ===
                      PRAKTIK_KINERJA_MAX_SATUAN_PENDIDIKAN_SELECTION
                    ) {
                      toast({
                        message:
                          'Batal pilih salah satu satuan pendidikan terlebih dahulu.',
                      })
                      return
                    }

                    if (!value.includes(id)) {
                      onChange([...value, id])
                    }
                  }

                  const handleRemove = (id: string) => {
                    if (value.includes(id)) {
                      onChange(value.filter((item) => item !== id))
                    }
                  }

                  return (
                    <>
                      {satuanPendidikanData.map(
                        ({ npsn, satuanPendidikan, scoreMetric }, index) => {
                          const isSelected = value.includes(npsn)
                          return (
                            <tr
                              key={index}
                              className={clsx(
                                'border-b border-subdued',
                                isSelected && '!bg-surface-selected-default'
                              )}
                            >
                              <td className="p-4">{satuanPendidikan}</td>
                              <td className="p-4">
                                <Badge
                                  variant={
                                    SCORE_COLORS[
                                      scoreMetric
                                    ] as BadgeProps['variant']
                                  }
                                >
                                  {SCORE_TRANSLATION[scoreMetric]}
                                </Badge>
                              </td>
                              <td className="flex items-center p-4 space-x-1">
                                {(isPraktikKinerjaEditable || isSelected) && (
                                  <Button
                                    data-testid="satuan-pendidikan-selection-item-add"
                                    color={isSelected ? 'black' : 'white'}
                                    size="sm"
                                    className="flex items-center space-x-1"
                                    onClick={() => handleChange(npsn)}
                                  >
                                    {isSelected && (
                                      <Icon color="inverse" fontSize="sm">
                                        check
                                      </Icon>
                                    )}
                                    <span>
                                      {isSelected ? 'Dipilih' : 'Pilih'}
                                    </span>
                                  </Button>
                                )}
                                {isPraktikKinerjaEditable && isSelected && (
                                  <Button
                                    data-testid="satuan-pendidikan-selection-item-remove"
                                    size="sm"
                                    variant="ghost"
                                    className="flex items-center space-x-1"
                                    onClick={() => handleRemove(npsn)}
                                  >
                                    <Icon color="informational" fontSize="sm">
                                      delete
                                    </Icon>
                                    <span>Batal pilih</span>
                                  </Button>
                                )}
                              </td>
                            </tr>
                          )
                        }
                      )}
                    </>
                  )
                }}
              />
            </tbody>
          </table>
        </>
      )}

      <DialogUpdateSatuanPendidikanInfo
        isOpen={isDialogUpdateInfoOpen}
        onClose={() => setDialogUpdateInfoOpen(false)}
      />
    </div>
  )
}

type TTabContentSummaryProps = {
  onChange: (index: number) => void
}
const TabContentSummary: React.FC<TTabContentSummaryProps> = ({ onChange }) => {
  const { watch } = useFormContext()
  const selectedPraktikKinerjaId = watch('selectedPraktikKinerjaId')
  const selectedSatuanPendidikanIds = watch('selectedSatuanPendidikanIds')

  const { praktikKinerjaData } = usePraktikKinerja()
  const { satuanPendidikanData } = useSatuanPendidikan({
    rhkId: selectedPraktikKinerjaId,
  })
  const { isPraktikKinerjaEditable } = usePerencanaanPS()

  const selectedPraktikKinerja = praktikKinerjaData.find(
    ({ id }) => id === selectedPraktikKinerjaId
  )
  const selectedSatuanPendidikan = satuanPendidikanData.filter(({ npsn }) =>
    (selectedSatuanPendidikanIds ?? []).includes(npsn)
  )
  const hasSelectedPraktikKinerja = Boolean(selectedPraktikKinerjaId)
  const hasSelectedSatuanPendidikan = selectedSatuanPendidikanIds.length > 0

  return (
    <div className="space-y-6">
      <Text variant="heading-lg">Berikut Perencanaan Anda:</Text>

      <div className="grid grid-cols-2 gap-x-4">
        <CardSummaryPraktikKinerja title="Anda memilih Praktik Kinerja:">
          <div className="flex flex-col items-start justify-between h-full px-6 py-4 space-y-6">
            {hasSelectedPraktikKinerja ? (
              <>
                <div className={clsx(styles.PraktikKinerjaItem, 'space-y-1')}>
                  <Text variant="heading-sm">
                    {selectedPraktikKinerja.nama}
                  </Text>
                  <Text>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: (
                          selectedPraktikKinerja?.keterangan ?? ''
                        ).replace(/\\n/g, '<br />'),
                      }}
                    />
                  </Text>
                </div>

                {isPraktikKinerjaEditable && (
                  <Button color="white" size="sm" onClick={() => onChange(0)}>
                    Ubah
                  </Button>
                )}
              </>
            ) : (
              <>
                <Text color="subdued">
                  Belum dipilih. Nanti pilihan Anda akan muncul di sini.
                </Text>

                <Button color="black" onClick={() => onChange(0)}>
                  Pilih Praktik Kinerja
                </Button>
              </>
            )}
          </div>
        </CardSummaryPraktikKinerja>

        <CardSummaryPraktikKinerja title="Untuk difokuskan di satuan pendidikan:">
          <div className="flex flex-col items-start justify-between h-full px-6 py-4 space-y-6">
            {hasSelectedSatuanPendidikan ? (
              <>
                <div>
                  <ol className="pl-6 list-disc">
                    {selectedSatuanPendidikan.map(
                      ({ satuanPendidikan }, index) => (
                        <li key={index}>
                          <Text>{satuanPendidikan}</Text>
                        </li>
                      )
                    )}
                  </ol>
                </div>

                {isPraktikKinerjaEditable && (
                  <Button color="white" size="sm" onClick={() => onChange(1)}>
                    Ubah
                  </Button>
                )}
              </>
            ) : (
              <>
                <Text color="subdued">
                  Belum dipilih. Nanti pilihan Anda akan muncul di sini.
                </Text>

                <Button color="black" onClick={() => onChange(1)}>
                  Pilih satuan pendidikan
                </Button>
              </>
            )}
          </div>
        </CardSummaryPraktikKinerja>
      </div>
    </div>
  )
}

type PraktikKinerjaProps = {}
const PraktikKinerja: React.FC<PraktikKinerjaProps> = ({}) => {
  const router = useRouter()
  const toast = useToast()

  const { trackEvent } = usePPSTrackEvent()

  // set Store by User ID
  useStoreByUserId()

  const [selectedIndex, setSelectedIndex] = React.useState<number>(0)
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] =
    React.useState<boolean>(false)
  const [isExitPreventionDialogOpen, setExitPreventionDialogOpen] =
    React.useState<boolean>(false)
  const [isInfoDialogOpen, setInfoDialogOpen] = React.useState<boolean>(false)

  const { period } = usePeriodContext()

  // Left Sidebar Hints
  const activeHints = PRAKTIK_KINERJA_HINTS[selectedIndex]

  // Form Data
  const formMethods = useForm<TPraktikKinerjaForm>({
    mode: 'onChange',
    defaultValues: {
      selectedPraktikKinerjaId: undefined,
      selectedSatuanPendidikanIds: [],
    },
  })
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
    watch,
  } = formMethods
  const selectedPraktikKinerjaId = watch('selectedPraktikKinerjaId')
  const selectedSatuanPendidikanIds = watch('selectedSatuanPendidikanIds')
  const prevSelectedPraktikKinerjaId = usePrevious(selectedPraktikKinerjaId)

  // Summary
  const { perencanaan, isPraktikKinerjaEditable, apiStatus } =
    usePerencanaanPS()

  // Satuan Pendidikan
  const { satuanPendidikanData } = useSatuanPendidikan({
    rhkId: selectedPraktikKinerjaId,
  })
  const maxSatuanPendidikanSelection =
    satuanPendidikanData.length >
    PRAKTIK_KINERJA_MAX_SATUAN_PENDIDIKAN_SELECTION
      ? PRAKTIK_KINERJA_MAX_SATUAN_PENDIDIKAN_SELECTION
      : satuanPendidikanData.length
  const remainingSatuanPendidikanSelectionCount =
    maxSatuanPendidikanSelection - selectedSatuanPendidikanIds.length
  const haveSelectedMaxSatuanPendidikan =
    satuanPendidikanData.length > 0 &&
    selectedSatuanPendidikanIds.length === maxSatuanPendidikanSelection

  const { hasDraft, draftSatuanPendidikan, draftPraktikKinerja, setDraft } =
    usePraktikKinerjaDraftStore()

  const isNextDisabled = React.useMemo(
    () =>
      (selectedIndex === 0 && !Boolean(selectedPraktikKinerjaId)) ||
      (selectedIndex === 2 && !isValid),
    [isValid, selectedIndex, selectedPraktikKinerjaId]
  )

  // API Submit Praktik Kinerja form
  const { isLoading: isSubmitting, mutateAsync } =
    useAPISubmitPerencanaan<TPerencanaanPraktikKinerjaRequestBody>(
      apiPaths.praktikKinerja
    )

  const handleBackStepAction = () => {
    if (selectedIndex === 0) {
      return
    }

    setSelectedIndex(selectedIndex - 1)
  }

  const handleNextStepAction = () => {
    // On Tab: 2. Satuan Pendidikan
    if (selectedIndex === 1 && !haveSelectedMaxSatuanPendidikan) {
      toast({
        message: `Wajib pilih ${
          satuanPendidikanData.length > 0 ? maxSatuanPendidikanSelection : ''
        } satuan pendidikan.`,
      })
      return
    }

    // On Tab: 3. Rangkuman
    if (selectedIndex === 2) {
      setConfirmationDialogOpen(true)
      return
    }

    setSelectedIndex(selectedIndex + 1)
  }

  const handleBackToPegawaiPSHubPage = () => {
    const isNewActivity = window.history.length <= 2
    if (isNewActivity) {
      router.push(`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/beranda`)
    } else {
      router.back()
    }
  }

  const handleSaveDraft = () => {
    setDraft(selectedPraktikKinerjaId, selectedSatuanPendidikanIds)

    // Track save draft
    trackEvent('perencanaan pegawai', 'save draft perencanaan', {
      aspect: 'praktik kinerja',
    })

    // Reset exit prevention
    resetPreventRouteChange()
    setExitPreventionDialogOpen(false)
    handleBackToPegawaiPSHubPage()
  }

  const submit = handleSubmit(async (formData) => {
    const { selectedPraktikKinerjaId, selectedSatuanPendidikanIds } = formData

    try {
      await mutateAsync({
        periodeId: period.id,
        praktikKinerjaRhkPsId: selectedPraktikKinerjaId,
        praktikKinerjaNpsn: selectedSatuanPendidikanIds,
      })

      // Track submission
      trackEvent('perencanaan pegawai', 'save draft perencanaan', {
        aspect: 'praktik kinerja',
      })

      // Reset states
      setConfirmationDialogOpen(false)
      resetPreventRouteChange()

      // Display toast
      toast({
        message: 'Berhasil simpan Praktik Kinerja!',
        type: 'success',
      })

      // Reditect to Beranda
      await router.push(
        `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/beranda`
      )

      // Reset draft
      setDraft(undefined, [])
    } catch (e) {
      setConfirmationDialogOpen(false)
      toast({
        message: 'Gagal simpan Praktik Kinerja. Coba lagi.',
      })
    }
  })

  const steps = [
    {
      title: '1. Praktik Kinerja',
      content: <TabContentPraktikKinerja />,
    },
    {
      title: '2. Satuan Pendidikan',
      content: (
        <TabContentSatuanPendidikan
          onBack={() => setSelectedIndex(0)}
          isValid={haveSelectedMaxSatuanPendidikan}
          remainingSelection={remainingSatuanPendidikanSelectionCount}
        />
      ),
    },
    {
      title: '3. Rangkuman',
      content: <TabContentSummary onChange={setSelectedIndex} />,
    },
  ]

  const { reset: resetPreventRouteChange } = useWarn({
    enabled: isDirty,
    bindPopState: true,
    onWarn: () => {
      setExitPreventionDialogOpen(true)
    },
  })

  React.useEffect(() => {
    if (
      Boolean(selectedPraktikKinerjaId) &&
      Boolean(prevSelectedPraktikKinerjaId) &&
      prevSelectedPraktikKinerjaId !== selectedPraktikKinerjaId
    ) {
      setValue('selectedSatuanPendidikanIds', [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPraktikKinerjaId])

  React.useEffect(() => {
    if (Boolean(draftPraktikKinerja)) {
      setValue('selectedPraktikKinerjaId', draftPraktikKinerja)
    } else if (
      Boolean(perencanaan?.praktikKinerjaRhkPs?.id) &&
      perencanaan?.praktikKinerjaRhkPs?.id !== '0'
    ) {
      setValue('selectedPraktikKinerjaId', perencanaan.praktikKinerjaRhkPs.id)
    }

    if ((draftSatuanPendidikan ?? []).length > 0) {
      setValue('selectedSatuanPendidikanIds', draftSatuanPendidikan)
    } else if ((perencanaan?.praktikKinerjaSatuanPendidikan ?? []).length > 0) {
      setValue(
        'selectedSatuanPendidikanIds',
        perencanaan.praktikKinerjaSatuanPendidikan.map(({ npsn }) => npsn)
      )
    }

    return () => {
      setValue('selectedPraktikKinerjaId', undefined)
      setValue('selectedSatuanPendidikanIds', [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasDraft,
    perencanaan?.praktikKinerjaRhkPs,
    perencanaan?.praktikKinerjaSatuanPendidikan,
    draftPraktikKinerja,
    draftSatuanPendidikan,
  ])

  React.useEffect(() => {
    if ((satuanPendidikanData ?? []).length > 0) {
      setValue(
        'selectedSatuanPendidikanIds',
        (selectedSatuanPendidikanIds ?? []).filter((id) =>
          satuanPendidikanData.map(({ npsn }) => npsn).includes(id)
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satuanPendidikanData])

  return (
    <div className="pb-20">
      <Header title="Praktik Kinerja" className="mb-9 print:hidden" />

      {apiStatus === 'loading' && (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}

      {apiStatus === 'success' && (
        <>
          <div className="grid grid-cols-10 gap-x-3">
            <div className="col-span-3">
              <Card className="sticky bg-white border rounded-md border-subdued top-24">
                <CardBody className="p-6 space-y-6">
                  <Text variant="heading-sm" className="flex-1">
                    {activeHints.title}
                  </Text>
                  <div className="space-y-4 text-left">
                    {activeHints.hints.map((hint, index) => (
                      <div key={index} className="flex space-x-3">
                        {activeHints.type === 'bullet' ? (
                          <Icon>check_circle_outlined</Icon>
                        ) : (
                          <Text>{index + 1}. </Text>
                        )}
                        <div className="space-y-1">
                          <Text>{hint}</Text>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    color="white"
                    onClick={() => setInfoDialogOpen(true)}
                    size="md"
                    variant="ghost"
                    fullWidth
                    className="!text-blue-70 !outline-none space-x-1"
                  >
                    <Icon color="informational">help</Icon>
                    <span>Tentang Praktik Kinerja</span>
                  </Button>
                </CardBody>
              </Card>
            </div>

            <FormProvider {...formMethods}>
              <div className="col-span-7">
                <CustomTabSteps
                  selectedIndex={selectedIndex}
                  onChange={(index) => setSelectedIndex(index)}
                  steps={steps}
                />
              </div>
            </FormProvider>
          </div>

          <DialogPraktikKinerjaConfirmation
            isOpen={isConfirmationDialogOpen}
            onConfirm={submit}
            onClose={() => {
              setConfirmationDialogOpen(false)
            }}
          />

          <DialogExitPrevention
            isOpen={isExitPreventionDialogOpen}
            onConfirm={handleSaveDraft}
            onClose={() => {
              setExitPreventionDialogOpen(false)
            }}
          />

          <DialogPraktikKinerjaInfo
            isOpen={isInfoDialogOpen}
            onClose={() => {
              setInfoDialogOpen(false)
            }}
          />

          {isPraktikKinerjaEditable && (
            <FooterActionV2
              hasBackButton
              isBackDisabled={selectedIndex === 0}
              isNextDisabled={isNextDisabled || isSubmitting}
              nextLabel="Selanjutnya"
              onBack={handleBackStepAction}
              onNext={handleNextStepAction}
            />
          )}

          <ScrollDown />
        </>
      )}
    </div>
  )
}
export default PraktikKinerja
